// La demande de dérogation reçue d'un autre club (D-137, maquettes d4 validées), reliée au match (Idries : « la demande
// venant de l'extérieur, j'ai l'impression que le lien avec le match n'est pas pris en compte ») : le club (pris dans le
// calendrier), le match (aller ou retour contre lui), ce qu'il demande et la salle vérifiée, pourquoi, comment elle est
// arrivée et de qui, « Demander l'avis du coach ». Elle suit ensuite les mêmes étapes : notre réponse, la saisie FBI (par
// le club demandeur), la confirmation sur FFBB.
import { clubUpcomingMatches, createDerogIn, teamMatches, type TeamMatch } from '../lib/api';
import { directoryContact } from '../lib/annuaire';
import { REASONS, REASON_LABEL, dayShort, hm, isEmail, matchTitle, type DerogReason, type Slot } from '../domain/derogation';
import { localDate } from '../domain/schedule';
import { squadName, type Ctx } from './context';
import { $, $$, html, mount, openSheet, toast } from './dom';
import { coachLabel } from './derogation-pre';
import { showHall } from './derogation-track';

const two = (n: number) => String(n).padStart(2, '0');
const hhmm = (d: Date) => `${two(d.getHours())}:${two(d.getMinutes())}`;
const VIAS: [string, string][] = [['email', 'Par e-mail'], ['phone', 'Par téléphone'], ['fbi', 'Sur FBI'], ['voice', 'De vive voix']];
const norm = (s: string) => s.trim().toLowerCase();

/** d4 : enregistrer une demande reçue. `eventId` : le match déjà choisi (fiche du match). */
export async function openRecueSheet(ctx: Ctx, opts: { eventId?: string; clubId?: string }, onDone: () => void): Promise<void> {
  const clubId = opts.clubId ?? ctx.adminOf.find(c => c.id === ctx.club?.id)?.id ?? ctx.adminOf[0]?.id ?? ctx.club?.id ?? '';
  const teamIds = ctx.teams.filter(t => t.club_id === clubId).map(t => t.id);
  const teamName = (id: string) => ctx.teams.find(t => t.id === id)?.name ?? '';
  const up = (await clubUpcomingMatches(teamIds, 240).catch(() => [] as TeamMatch[])).filter(m => !m.events.cancelled_at && new Date(m.events.starts_at) > new Date());
  const label = (m: TeamMatch) => squadName(ctx, m.squad_id) ?? teamName(m.events.team_id);
  const title = (m: TeamMatch) => matchTitle(label(m), m.opponent, m.is_home);
  const preset = opts.eventId ? up.find(m => m.event_id === opts.eventId) : undefined;
  // Les clubs du calendrier, et leurs matchs à venir.
  const clubs = [...up.reduce((acc, m) => acc.set(norm(m.opponent), [...(acc.get(norm(m.opponent)) ?? []), m]), new Map<string, TeamMatch[]>()).values()]
    .map(ms => ({ name: ms[0]!.opponent, matches: ms })).sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  openSheet('Demande reçue d’un autre club', html`<form class="form" id="f-in" novalidate>
    <div class="field"><span>Le club qui demande</span><div id="in-club"></div></div>
    <div id="in-rest" class="form"></div>
  </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-in')!;
    let match: TeamMatch | null = null;
    let kind: 'moved' | 'swap' = 'moved';
    let reason: DerogReason = 'effectif';
    let via: 'email' | 'phone' | 'fbi' | 'voice' = 'email';
    let email: string | null = null;

    const pickClub = () => {
      mount($(f, '#in-club')!, clubs.length ? html`
        <div class="pick-tools"><input type="search" class="pick-search" placeholder="Chercher un club du calendrier" aria-label="Chercher un club" autocomplete="off"></div>
        <ul class="pick-list" id="in-clubs">${clubs.map(c => html`<li data-name="${norm(c.name)}"><button type="button" data-club="${c.name}">
          <span class="pick-main"><strong>${c.name}</strong><span class="muted small">${c.matches.map(m => `${label(m)} ${m.is_home === false ? 'chez lui' : 'chez nous'}`).join(' · ')}</span></span>
          <span class="chip chip-mute">${c.matches.length} match${c.matches.length > 1 ? 's' : ''}</span></button></li>`)}</ul>`
        : html`<p class="muted">Aucun match à venir dans le calendrier.</p>`);
      mount($(f, '#in-rest')!, html``);
      $<HTMLInputElement>(f, '.pick-search')?.addEventListener('input', e => {
        const q = norm((e.target as HTMLInputElement).value);
        for (const li of $$<HTMLElement>(f, '#in-clubs li')) li.hidden = !!q && !li.dataset.name!.includes(q);
      });
      for (const b of $$<HTMLButtonElement>(f, '[data-club]')) b.addEventListener('click', () => chooseClub(b.dataset.club!));
    };

    const chooseClub = (name: string, pre?: TeamMatch) => {
      const c = clubs.find(x => norm(x.name) === norm(name))!;
      match = pre ?? c.matches[0] ?? null;
      email = null;
      mount($(f, '#in-club')!, html`<div class="card dp-found cn-found"><p class="eyebrow cn-ok">✓ Dans ton calendrier · ${c.matches.length} match${c.matches.length > 1 ? 's' : ''}</p>
        <span class="cn-found-name">${c.name}</span>
        <span class="muted small"><span id="in-mail">annuaire des clubs…</span>${pre ? '' : html` · <button type="button" class="link-x" id="in-change">changer</button>`}</span>
        <label class="field" id="in-mail-field" hidden><span>E-mail pour lui répondre (facultatif)</span><input name="email" type="email" inputmode="email" maxlength="200" autocomplete="off"></label></div>`);
      $(f, '#in-change')?.addEventListener('click', pickClub);
      renderRest(c.matches);
    };

    const renderRest = (matches: TeamMatch[]) => {
      if (!match) return;
      const m0 = match;
      const start = new Date(m0.events.starts_at);
      mount($(f, '#in-rest')!, html`
        <div class="field"><span>Sur quel match ?</span>
          <ul class="pick-list" id="in-matches">${matches.map(m => html`<li><button type="button" data-m="${m.event_id}" class="${m.event_id === m0.event_id ? 'on' : ''}">
            <span class="pick-main"><strong>${title(m)}</strong><span class="muted small">${dayShort(m.events.starts_at)} à ${hm(m.events.starts_at)}${m.events.location ? ` · ${m.events.location}` : ''}</span></span>
            <span class="chip ${m.event_id === m0.event_id ? 'chip-ok' : 'chip-mute'}">${m.event_id === m0.event_id ? '✓ Choisi' : 'Choisir'}</span></button></li>`)}</ul></div>
        ${m0.is_home !== null ? html`<div class="field"><span>Ce qu’il demande</span>
          <div class="seg" role="radiogroup" aria-label="Ce qu’il demande"><button type="button" role="radio" data-kind="moved" aria-pressed="${String(kind === 'moved')}">Date, heure ou salle</button>
            <button type="button" role="radio" data-kind="swap" aria-pressed="${String(kind === 'swap')}">Inverser domicile / extérieur</button></div></div>` : ''}
        <ol class="call-slots"><li class="call-slot" id="in-slot"><span class="eyebrow">${kind === 'swap' ? 'Nouvelle date de ce match (facultatif)' : 'Créneau demandé'}</span>
          <div class="call-slot-when"><input type="date" name="d" value="${localDate(start)}" aria-label="Date demandée"><input type="time" name="t" value="${hhmm(start)}" aria-label="Heure demandée"></div>
          <input name="label" class="dp-full" maxlength="200" value="${m0.events.location ?? ''}" placeholder="Salle" aria-label="Salle demandée"></li></ol>
        <p class="dg-free" id="in-hall" hidden></p>
        <div class="field"><span>Pourquoi, d’après lui</span>
          <div class="chip-toggles" role="radiogroup" aria-label="Pourquoi">${REASONS.map(r => html`<button type="button" class="chip-toggle" role="radio" data-reason="${r}" aria-pressed="${String(r === reason)}">${REASON_LABEL[r]}</button>`)}</div>
          <input name="why" maxlength="200" placeholder="ex. effectif réduit ce week-end" aria-label="En deux mots"></div>
        <div class="field"><span>Reçue</span>
          <div class="chip-toggles" role="radiogroup" aria-label="Comment elle est arrivée">${VIAS.map(([k, l]) => html`<button type="button" class="chip-toggle" role="radio" data-via="${k}" aria-pressed="${String(k === via)}">${l}</button>`)}</div>
          <input name="from" maxlength="120" placeholder="De qui : ex. Julien R., secrétaire" aria-label="De qui"></div>
        <label class="check" id="in-coach" hidden><input type="checkbox" name="coach" checked><span><strong id="in-coach-name"></strong><br>
          <span class="muted small">Il répond d’un geste ; tu décides ensuite.</span></span></label>
        <p class="form-msg" role="alert"></p>
        <button type="submit" class="btn btn-primary">Enregistrer la demande</button>
        <p class="hint">Reliée au match ${title(m0)} : elle suit les mêmes étapes, jusqu’à la confirmation sur FFBB.</p>`);
      // La salle demandée est-elle libre (match à domicile) ?
      const check = () => {
        const d = $<HTMLInputElement>(f, 'input[name="d"]')!.value, t = $<HTMLInputElement>(f, 'input[name="t"]')!.value;
        const el = $(f, '#in-hall');
        if (m0.is_home === false && kind === 'moved') { if (el) el.hidden = true; return; }
        void showHall(el, clubId, $<HTMLInputElement>(f, '#in-slot input[name="label"]')!.value || m0.events.location, new Date(`${d}T${t}:00`),
          { exclude: [m0.event_id], teamName, squadName: (q: string | null) => squadName(ctx, q) });
      };
      $(f, '#in-slot')!.addEventListener('input', check);
      check();
      // L'e-mail du club : l'annuaire des clubs (D-094), modifiable.
      void directoryContact(m0.event_id).then(c => {
        email = c?.email ?? null;
        const s = $(f, '#in-mail'); if (s) s.textContent = email ? `${email} · annuaire des clubs` : 'pas d’e-mail dans l’annuaire';
        const fld = $(f, '#in-mail-field'); if (fld) fld.hidden = !!email;
      }).catch(() => { const fld = $(f, '#in-mail-field'); if (fld) fld.hidden = false; });
      void coachLabel(ctx, { teamId: m0.events.team_id, clubId, team: label(m0) }).then(l => {
        if (!l) return;
        $(f, '#in-coach-name')!.textContent = `Demander l’avis de ${l}`;
        $(f, '#in-coach')!.hidden = false;
      });
      for (const b of $$<HTMLButtonElement>(f, '[data-m]')) b.addEventListener('click', () => { match = matches.find(x => x.event_id === b.dataset.m) ?? match; renderRest(matches); });
      for (const b of $$<HTMLButtonElement>(f, '[data-kind]')) b.addEventListener('click', () => { kind = b.dataset.kind === 'swap' ? 'swap' : 'moved'; renderRest(matches); });
      for (const b of $$<HTMLButtonElement>(f, '[data-reason]')) b.addEventListener('click', () => {
        reason = b.dataset.reason as DerogReason; $$(f, '[data-reason]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      });
      for (const b of $$<HTMLButtonElement>(f, '[data-via]')) b.addEventListener('click', () => {
        via = b.dataset.via as typeof via; $$(f, '[data-via]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      });
    };

    f.addEventListener('submit', async e => {
      e.preventDefault();
      const msg = $(f, '.form-msg');
      if (!match || !msg) return;
      msg.textContent = '';
      const d = $<HTMLInputElement>(f, 'input[name="d"]')!.value, t = $<HTMLInputElement>(f, 'input[name="t"]')!.value;
      const at = new Date(`${d}T${t}:00`);
      const loc = $<HTMLInputElement>(f, '#in-slot input[name="label"]')!.value.trim() || null;
      const changed = !isNaN(at.getTime()) && (at.getTime() !== new Date(match.events.starts_at).getTime() || (loc ?? '') !== (match.events.location ?? ''));
      if (kind === 'moved' && (!d || !t || isNaN(at.getTime()))) { msg.textContent = 'Indique la date et l’heure demandées.'; return; }
      if (kind === 'moved' && !changed) { msg.textContent = 'Rien ne change : modifie la date, l’heure ou la salle demandées.'; return; }
      if (changed && at <= new Date()) { msg.textContent = 'Le créneau demandé doit être à venir.'; return; }
      const slots: Slot[] = kind === 'moved' || changed ? [{ starts_at: at.toISOString(), location: loc }] : [];
      const why = String(new FormData(f).get('why') ?? '').trim() || null;
      if (reason === 'autre' && !why) { msg.textContent = 'Dis en deux mots pourquoi.'; return; }
      const typed = String(new FormData(f).get('email') ?? '').trim().toLowerCase();
      if (typed && !isEmail(typed)) { msg.textContent = 'Adresse e-mail invalide.'; return; }
      let pairId: string | null = null;
      if (kind === 'swap') {
        const all = await teamMatches(match.events.team_id).catch(() => [] as TeamMatch[]);
        pairId = all.find(x => x.event_id !== match!.event_id && !x.events.cancelled_at && x.is_home !== null && x.is_home !== match!.is_home
          && norm(x.opponent) === norm(match!.opponent))?.event_id ?? null;
      }
      const btn = $<HTMLButtonElement>(f, 'button[type="submit"]')!;
      btn.disabled = true;
      try {
        const id = await createDerogIn({
          eventId: match.event_id, kind, pairId, slots, reason, reasonNote: why, opponentClub: match.opponent, email: typed || email,
          via, from: String(new FormData(f).get('from') ?? '').trim() || null,
          askCoach: !$(f, '#in-coach')!.hidden && !!$<HTMLInputElement>(f, 'input[name="coach"]')?.checked,
        });
        close();
        toast('Demande enregistrée, reliée au match');
        location.hash = `#/derogations/${id}`;
        onDone();
      } catch (err) {
        const m = String((err as Error)?.message ?? '');
        msg.textContent = m.includes('déjà en cours') ? 'Une dérogation est déjà en cours sur ce match : retrouve-la dans Dérogations.'
          : m.includes('créneau') ? 'Vérifie le créneau demandé : à venir, et différent du match actuel.' : 'Enregistrement impossible pour le moment.';
        btn.disabled = false;
      }
    });

    if (preset) chooseClub(preset.opponent, preset); else pickClub();
  });
}
